import { projectsProjectReferenceSnapshotSchema } from '@crest/module-projects/contracts/project-reference';
import { platformStorageAttachedObjectReferenceSchema, platformStorageSha256Schema, platformStorageUploadIntentIdSchema, platformStorageUploadTransportSchema, } from '@crest/platform-contracts/storage';
import { z } from 'zod';
export const financeExpensesCapability = 'finance.expenses.v1';
export const financeReceiptAnalysisCapability = 'finance.receipt-analysis.v1';
export const financeExpenseIdSchema = z.string().uuid();
export const financeExpenseMoneyCentsSchema = z.number().int().min(0).max(1_000_000_000_000_000);
export const financeExpenseScopeSchema = z.enum([
    'job', 'shop', 'fleet', 'overhead', 'reimbursement', 'other',
]);
export const financeExpenseCategorySchema = z.enum([
    'materials', 'equipment', 'fuel', 'subcontractor', 'meals', 'office', 'travel', 'other',
]);
export const financeExpensePaymentMethodSchema = z.enum([
    'company-card', 'personal-card', 'cash', 'check', 'other',
]);
export const financeExpenseStatusSchema = z.enum([
    'pending', 'flagged', 'approved', 'rejected', 'void',
]);
export const financeExpenseReceiptContentTypeSchema = z.string().trim().max(100).regex(/^image\/[a-z0-9][a-z0-9.+-]*$/, 'Expense receipts must use an image content type.');
export const financeExpenseReceiptFileNameSchema = z.string().trim().min(1).max(240).superRefine((value, context) => {
    if (value === '.' || value === '..' || /[\\/\u0000-\u001f\u007f]/.test(value)) {
        context.addIssue({ code: 'custom', message: 'Expense receipt file name is not safe.' });
    }
});
const nullableTrimmedText = (maximum) => z.string().trim().max(maximum).nullable();
const financeExpenseLineBaseSchema = z.strictObject({
    name: z.string().trim().min(1).max(200),
    quantityTenThousandths: z.number().int().min(0).max(10_000_000_000),
    unitPriceCents: financeExpenseMoneyCentsSchema,
    lineTotalCents: financeExpenseMoneyCentsSchema,
});
export const financeExpenseLineInputSchema = financeExpenseLineBaseSchema.omit({
    lineTotalCents: true,
});
export const financeExpenseLineSchema = financeExpenseLineBaseSchema.superRefine((line, context) => {
    const expected = Number((BigInt(line.quantityTenThousandths) * BigInt(line.unitPriceCents) + 5000n) / 10000n);
    if (line.lineTotalCents !== expected) {
        context.addIssue({
            code: 'custom',
            path: ['lineTotalCents'],
            message: 'Expense line total must reconcile to quantity and unit price.',
        });
    }
});
export const financeExpenseDraftSchema = z.strictObject({
    projectId: z.string().uuid().nullable().default(null),
    scope: financeExpenseScopeSchema,
    expenseDate: z.string().date(),
    vendor: nullableTrimmedText(300).default(null),
    description: nullableTrimmedText(2_000).default(null),
    category: financeExpenseCategorySchema,
    paymentMethod: financeExpensePaymentMethodSchema,
    cardLastFour: z.string().regex(/^\d{4}$/).nullable().default(null),
    subtotalCents: financeExpenseMoneyCentsSchema,
    taxCents: financeExpenseMoneyCentsSchema,
    totalCents: financeExpenseMoneyCentsSchema,
    lineItems: z.array(financeExpenseLineInputSchema).max(100).default([]),
    receiptObjectIds: z.array(z.string().uuid()).max(20).default([]),
    costCenterCode: z.string().trim().min(1).max(50).nullable().default(null),
    accountCategoryCode: z.string().trim().min(1).max(80).nullable().default(null),
}).superRefine((expense, context) => {
    if (expense.scope === 'job' && expense.projectId === null) {
        context.addIssue({
            code: 'custom',
            path: ['projectId'],
            message: 'A job expense requires a Project reference.',
        });
    }
    if (expense.scope !== 'job' && expense.projectId !== null) {
        context.addIssue({
            code: 'custom',
            path: ['projectId'],
            message: 'Only job expenses can retain a Project reference.',
        });
    }
    if (expense.totalCents !== expense.subtotalCents + expense.taxCents) {
        context.addIssue({
            code: 'custom',
            path: ['totalCents'],
            message: 'Expense total must equal subtotal plus tax.',
        });
    }
    if (expense.lineItems.length > 0 &&
        expense.lineItems.reduce((total, line) => total + Number((BigInt(line.quantityTenThousandths) * BigInt(line.unitPriceCents) + 5000n) / 10000n), 0) !== expense.subtotalCents) {
        context.addIssue({
            code: 'custom',
            path: ['lineItems'],
            message: 'Expense lines must reconcile to the subtotal.',
        });
    }
    const cardPayment = expense.paymentMethod === 'company-card' ||
        expense.paymentMethod === 'personal-card';
    if (!cardPayment && expense.cardLastFour !== null) {
        context.addIssue({
            code: 'custom',
            path: ['cardLastFour'],
            message: 'Card digits can be retained only for card payments.',
        });
    }
});
const financeExpenseVisibleMoneySchema = financeExpenseMoneyCentsSchema.nullable();
const financeExpenseLineResponseSchema = financeExpenseLineBaseSchema.extend({
    unitPriceCents: financeExpenseVisibleMoneySchema,
    lineTotalCents: financeExpenseVisibleMoneySchema,
});
export const financeExpenseContentSchema = z.strictObject({
    projectReference: projectsProjectReferenceSnapshotSchema.nullable(),
    scope: financeExpenseScopeSchema,
    expenseDate: z.string().date(),
    vendor: nullableTrimmedText(300),
    description: nullableTrimmedText(2_000),
    category: financeExpenseCategorySchema,
    paymentMethod: financeExpensePaymentMethodSchema,
    cardLastFour: z.string().regex(/^\d{4}$/).nullable(),
    subtotalCents: financeExpenseVisibleMoneySchema,
    taxCents: financeExpenseVisibleMoneySchema,
    totalCents: financeExpenseVisibleMoneySchema,
    lineItems: z.array(financeExpenseLineResponseSchema).max(100),
    receiptReferences: z.array(platformStorageAttachedObjectReferenceSchema).max(20),
    costCenterCode: nullableTrimmedText(50),
    accountCategoryCode: nullableTrimmedText(80),
});
export const financeReceiptAnalysisFlagSchema = z.enum([
    'name-missing',
    'name-mismatch',
    'low-confidence',
    'amount-unclear',
    'amount-mismatch',
    'scan-failed',
    'enqueue-failed',
]);
export const financeReceiptAnalysisRequestStatusSchema = z.enum([
    'requested', 'processing', 'succeeded', 'failed', 'disabled', 'cancelled',
]);
export const financeReceiptAnalysisSourceSchema = z.strictObject({
    kind: z.enum(['manual', 'adapter']),
    providerKey: z.string().trim().min(1).max(100).nullable(),
    modelKey: z.string().trim().min(1).max(160).nullable(),
});
export const financeReceiptAnalysisExtractionSchema = z.strictObject({
    vendor: nullableTrimmedText(300),
    description: nullableTrimmedText(2_000),
    subtotalCents: financeExpenseMoneyCentsSchema.nullable(),
    taxCents: financeExpenseMoneyCentsSchema.nullable(),
    totalCents: financeExpenseMoneyCentsSchema.nullable(),
    expenseDate: z.string().date().nullable(),
    paymentMethod: financeExpensePaymentMethodSchema.nullable(),
    cardLastFour: z.string().regex(/^\d{4}$/).nullable(),
    category: financeExpenseCategorySchema.nullable(),
    lineItems: z.array(financeExpenseLineInputSchema).max(100).nullable(),
    personName: nullableTrimmedText(200),
});
export const financeReceiptAnalysisSummarySchema = z.strictObject({
    requestId: z.string().uuid(),
    objectId: z.string().uuid(),
    status: financeReceiptAnalysisRequestStatusSchema,
    attemptCount: z.number().int().nonnegative(),
    maximumAttempts: z.number().int().min(1).max(20),
    confidenceBasisPoints: z.number().int().min(0).max(10_000).nullable(),
    flags: z.array(financeReceiptAnalysisFlagSchema).max(20),
    source: financeReceiptAnalysisSourceSchema.nullable(),
    requestedAt: z.string().datetime({ offset: true }),
    completedAt: z.string().datetime({ offset: true }).nullable(),
    failureCode: z.string().trim().min(1).max(100).nullable(),
});
export const financeExpenseSchema = z.strictObject({
    contractVersion: z.literal(financeExpensesCapability),
    tenantId: z.string().uuid(),
    id: financeExpenseIdSchema,
    revision: z.number().int().positive(),
    status: financeExpenseStatusSchema,
    amountsVisible: z.boolean(),
    submittedByMembershipId: z.string().uuid(),
    decidedByMembershipId: z.string().uuid().nullable(),
    decidedAt: z.string().datetime({ offset: true }).nullable(),
    decisionReason: nullableTrimmedText(2_000),
    voidedByMembershipId: z.string().uuid().nullable(),
    voidedAt: z.string().datetime({ offset: true }).nullable(),
    createdAt: z.string().datetime({ offset: true }),
    updatedAt: z.string().datetime({ offset: true }),
    contentHash: z.string().regex(/^[a-f0-9]{64}$/),
    content: financeExpenseContentSchema,
    receiptAnalysis: financeReceiptAnalysisSummarySchema.nullable(),
});
export const financeExpenseCreateCommandSchema = z.strictObject({
    kind: z.literal('create'),
    commandId: z.string().uuid(),
    expenseId: financeExpenseIdSchema,
    expense: financeExpenseDraftSchema,
    reason: z.string().trim().min(1).max(2_000),
});
export const financeExpenseReviseCommandSchema = z.strictObject({
    kind: z.literal('revise'),
    commandId: z.string().uuid(),
    expectedRevision: z.number().int().positive(),
    expense: financeExpenseDraftSchema,
    reason: z.string().trim().min(1).max(2_000),
});
export const financeExpenseDecisionCommandSchema = z.strictObject({
    kind: z.enum(['approve', 'reject', 'void']),
    commandId: z.string().uuid(),
    expectedRevision: z.number().int().positive(),
    reason: z.string().trim().min(1).max(2_000),
});
export const financeExpenseCommandSchema = z.discriminatedUnion('kind', [
    financeExpenseReviseCommandSchema,
    financeExpenseDecisionCommandSchema,
]);
export const financeExpenseBulkApproveCommandSchema = z.strictObject({
    kind: z.literal('bulk-approve'),
    batchId: z.string().uuid(),
    items: z.array(z.strictObject({
        commandId: z.string().uuid(),
        expenseId: financeExpenseIdSchema,
        expectedRevision: z.number().int().positive(),
    })).min(1).max(100).superRefine((items, context) => {
        const expenseIds = new Set(items.map(({ expenseId }) => expenseId));
        const commandIds = new Set(items.map(({ commandId }) => commandId));
        if (expenseIds.size !== items.length) {
            context.addIssue({ code: 'custom', message: 'Bulk expense IDs must be unique.' });
        }
        if (commandIds.size !== items.length) {
            context.addIssue({ code: 'custom', message: 'Bulk command IDs must be unique.' });
        }
    }),
    reason: z.string().trim().min(1).max(2_000),
});
export const financeExpenseBulkApproveResponseSchema = z.strictObject({
    data: z.strictObject({
        batchId: z.string().uuid(),
        approvedIds: z.array(financeExpenseIdSchema),
        idempotentIds: z.array(financeExpenseIdSchema),
        skippedSelfSubmittedIds: z.array(financeExpenseIdSchema),
        skippedStateOrRevisionIds: z.array(financeExpenseIdSchema),
        unavailableIds: z.array(financeExpenseIdSchema),
    }),
});
export const financeInitiateExpenseReceiptUploadSchema = z.strictObject({
    projectId: z.string().uuid().nullable().default(null),
    idempotencyKey: z.string().uuid(),
    originalFileName: financeExpenseReceiptFileNameSchema,
    contentType: financeExpenseReceiptContentTypeSchema,
    sizeBytes: z.number().int().positive().max(100 * 1_024 * 1_024),
    sha256: platformStorageSha256Schema,
});
export const financeExpenseReceiptUploadAuthorizationResponseSchema = z.strictObject({
    data: z.strictObject({
        expenseId: financeExpenseIdSchema,
        objectId: z.string().uuid(),
        uploadIntentId: platformStorageUploadIntentIdSchema,
        transport: platformStorageUploadTransportSchema,
    }),
});
export const financeFinalizeExpenseReceiptUploadSchema = z.strictObject({
    uploadIntentId: platformStorageUploadIntentIdSchema,
});
export const financeExpenseReceiptUploadResponseSchema = z.strictObject({
    data: platformStorageAttachedObjectReferenceSchema,
});
export const financeReceiptAnalysisRequestCommandSchema = z.strictObject({
    commandId: z.string().uuid(),
    requestId: z.string().uuid(),
    expectedExpenseRevision: z.number().int().positive(),
    objectId: z.string().uuid(),
    reason: z.string().trim().min(1).max(2_000),
});
export const financeReceiptAnalysisResultCommandSchema = z.strictObject({
    commandId: z.string().uuid(),
    requestId: z.string().uuid(),
    expectedExpenseRevision: z.number().int().positive(),
    confidenceBasisPoints: z.number().int().min(0).max(10_000),
    flags: z.array(financeReceiptAnalysisFlagSchema).max(20),
    source: financeReceiptAnalysisSourceSchema,
    extracted: financeReceiptAnalysisExtractionSchema,
    reason: z.string().trim().min(1).max(2_000),
});
export const financeReceiptAnalysisFailureCommandSchema = z.strictObject({
    commandId: z.string().uuid(),
    requestId: z.string().uuid(),
    expectedExpenseRevision: z.number().int().positive(),
    failureCode: z.string().trim().regex(/^[a-z][a-z0-9-]{0,99}$/),
    retryable: z.boolean(),
    source: financeReceiptAnalysisSourceSchema,
    reason: z.string().trim().min(1).max(2_000),
});
export const financeExpenseListQuerySchema = z.strictObject({
    projectId: z.string().uuid().nullable().default(null),
    status: financeExpenseStatusSchema.nullable().default(null),
    scope: financeExpenseScopeSchema.nullable().default(null),
    category: financeExpenseCategorySchema.nullable().default(null),
    paymentMethod: financeExpensePaymentMethodSchema.nullable().default(null),
    submittedByMembershipId: z.string().uuid().nullable().default(null),
    dateFrom: z.string().date().nullable().default(null),
    dateTo: z.string().date().nullable().default(null),
    vendor: z.string().trim().max(300).default(''),
    limit: z.number().int().min(1).max(250).default(50),
    offset: z.number().int().nonnegative().default(0),
}).superRefine((query, context) => {
    if (query.dateFrom !== null && query.dateTo !== null && query.dateTo < query.dateFrom) {
        context.addIssue({ code: 'custom', path: ['dateTo'], message: 'Date range is invalid.' });
    }
});
const financeExpenseSummaryStatusSchema = z.strictObject({
    count: z.number().int().nonnegative(),
    totalCents: financeExpenseVisibleMoneySchema,
});
export const financeExpenseListResponseSchema = z.strictObject({
    data: z.array(financeExpenseSchema),
    page: z.strictObject({
        limit: z.number().int().positive(),
        offset: z.number().int().nonnegative(),
        returned: z.number().int().nonnegative(),
        total: z.number().int().nonnegative(),
        hasMore: z.boolean(),
    }),
    summary: z.strictObject({
        amountsVisible: z.boolean(),
        total: financeExpenseSummaryStatusSchema,
        pending: financeExpenseSummaryStatusSchema,
        flagged: financeExpenseSummaryStatusSchema,
        approved: financeExpenseSummaryStatusSchema,
        rejected: financeExpenseSummaryStatusSchema,
        void: financeExpenseSummaryStatusSchema,
    }),
});
export const financeExpenseResponseSchema = z.strictObject({
    data: financeExpenseSchema,
    command: z.strictObject({
        commandId: z.string().uuid(),
        idempotent: z.boolean(),
    }).optional(),
});
//# sourceMappingURL=finance-expense.contract.js.map